import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ImageUploadService } from '../../services/image-upload.service';
import {
  UploadImageAndScan,
  SetUploadStatus,
  SetScanResult,
  ResetUploadState,
  ClearUploadedImages,
  RemoveUploadedImage,
} from './image-upload.actions';
import { ImageUploadStateModel, UploadedImage } from './image-upload.model';

@State<ImageUploadStateModel>({
  name: 'ImageUpload',
  defaults: {
    uploadedImages: [],
    isUploadInProgress: false,
    isScanInProgress: false,
    error: null,
  },
})
@Injectable({
  providedIn: 'root',
})
export class ImageUploadState {
  constructor(private readonly imageUploadService: ImageUploadService) {}

  @Action(UploadImageAndScan)
  async uploadImageAndScan(
    { patchState, getState }: StateContext<ImageUploadStateModel>,
    action: UploadImageAndScan
  ) {
    const { file, location } = action;
    
    // Validate file
    const validation = this.imageUploadService.validateFile(file);
    
    if (!validation.valid) {
      patchState({ error: validation.error || 'Invalid file' });
      return throwError(() => new Error(validation.error));
    }

    patchState({ 
      isUploadInProgress: true, 
      error: null,
      currentUpload: {
        file,
        progress: 0,
        status: 'Starting upload...'
      }
    });

    return this.imageUploadService.uploadImageAndScan(file, location).pipe(
      tap(response => {
        if (response.success) {
          // Create uploaded image object
          const uploadedImage: UploadedImage = {
            name: file.name,
            size: file.size,
            objectURL: response.data?.imageUrl || '', // Will be set from S3 response
            s3Key: response.data?.imageKey || response.scanId || '', // Will be set from S3 response
            uploading: false,
            uploaded: true,
            status: 'completed',
            scanResult: response,
            timestamp: new Date(),
          };

          const currentState = getState();
          const updatedImages = [...currentState.uploadedImages, uploadedImage];

          patchState({
            uploadedImages: updatedImages,
            isUploadInProgress: false,
            isScanInProgress: false,
            lastUploadedImage: uploadedImage,
            currentUpload: undefined,
            error: null,
          });
        } else {
          patchState({
            error: response.error || 'Scan failed',
            isUploadInProgress: false,
            isScanInProgress: false,
            currentUpload: undefined,
          });
        }
      }),
      catchError(error => {
        console.error('❌ Error in uploadImageAndScan action:', error);
        patchState({
          error: error.message || 'Upload failed',
          isUploadInProgress: false,
          isScanInProgress: false,
          currentUpload: undefined,
        });
        return throwError(() => error);
      })
    );
  }

  @Action(SetUploadStatus)
  setUploadStatus(
    { patchState, getState }: StateContext<ImageUploadStateModel>,
    action: SetUploadStatus
  ) {
    const { imageKey, status } = action;
    const currentState = getState();
    
    const updatedImages: UploadedImage[] = [];
    
    for (const image of currentState.uploadedImages) {
      if (image.s3Key === imageKey) {
        updatedImages.push({
          ...image,
          status,
          uploading: status === 'uploading'
        });
      } else {
        updatedImages.push(image);
      }
    }

    patchState({
      uploadedImages: updatedImages,
      isUploadInProgress: status === 'uploading',
      isScanInProgress: status === 'scanning',
    });
  }

  @Action(SetScanResult)
  setScanResult(
    { patchState, getState }: StateContext<ImageUploadStateModel>,
    action: SetScanResult
  ) {
    const { imageKey, result } = action;
    const currentState = getState();
    
    const updatedImages: UploadedImage[] = [];
    
    for (const image of currentState.uploadedImages) {
      if (image.s3Key === imageKey) {
        updatedImages.push({
          ...image,
          scanResult: result,
          status: 'completed'
        });
      } else {
        updatedImages.push(image);
      }
    }

    patchState({
      uploadedImages: updatedImages,
      isScanInProgress: false,
    });
  }

  @Action(RemoveUploadedImage)
  removeUploadedImage(
    { patchState, getState }: StateContext<ImageUploadStateModel>,
    action: RemoveUploadedImage
  ) {
    const currentState = getState();
    const updatedImages = currentState.uploadedImages.filter(
      image => image.s3Key !== action.image.s3Key
    );

    patchState({
      uploadedImages: updatedImages,
    });
  }

  @Action(ClearUploadedImages)
  clearUploadedImages({ patchState }: StateContext<ImageUploadStateModel>) {
    patchState({
      uploadedImages: [],
      lastUploadedImage: undefined,
    });
  }

  @Action(ResetUploadState)
  resetUploadState({ setState }: StateContext<ImageUploadStateModel>) {
    setState({
      uploadedImages: [],
      isUploadInProgress: false,
      isScanInProgress: false,
      currentUpload: undefined,
      error: null,
      lastUploadedImage: undefined,
    });
  }
} 